import React from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, Building2, MapPin } from 'lucide-react';
import { Usuario } from '../../types';

interface UserTableProps {
  users: Usuario[];
  selectedUsers: number[];
  onUserSelection: (userId: number) => void;
  onSelectAll: () => void;
  onUserDelete: (userId: number, userName: string) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  selectedUsers,
  onUserSelection,
  onSelectAll,
  onUserDelete,
  sortBy,
  sortOrder,
  onSort
}) => {
  const getStatusBadge = (status: number) => {
    return status === 1 ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300">
        Ativo
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-300">
        Inativo
      </span>
    );
  };

  const getNivelBadge = (nivel: string) => {
    const nivelConfig = {
      'admin': { label: 'Operador', bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-300' },
      'dev': { label: 'Programador', bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-800 dark:text-blue-300' },
      'usuario': { label: 'Gerente', bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-800 dark:text-gray-300' }
    };
    
    const config = nivelConfig[nivel as keyof typeof nivelConfig] || nivelConfig.usuario;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const renderOrganizationalInfo = (user: Usuario) => {
    if (user.nivel === 'admin' || user.nivel === 'dev') {
      // Mostrar departamentos para admin e dev
      if (user.departamentos && user.departamentos.length > 0) {
        return (
          <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
            <Building2 size={14} className="text-blue-500 dark:text-blue-400" />
            <span className="truncate">
              {user.departamentos.length === 1 
                ? user.departamentos[0].nome 
                : `${user.departamentos.length} departamentos`
              }
            </span>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
          <Building2 size={14} />
          <span>Sem departamento</span>
        </div>
      );
    } else {
      // Mostrar unidades para usuários
      if (user.unidades && user.unidades.length > 0) {
        return (
          <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
            <MapPin size={14} className="text-green-500 dark:text-green-400" />
            <span className="truncate">
              {user.unidades.length === 1 
                ? user.unidades[0].nome 
                : `${user.unidades.length} unidades`
              }
            </span>
          </div>
        );
      }
      return (
        <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
          <MapPin size={14} />
          <span>Sem unidade</span>
        </div>
      );
    }
  };

  const allSelected = users.length > 0 && selectedUsers.length === users.length;
  const someSelected = selectedUsers.length > 0 && selectedUsers.length < users.length;

  return (
    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={input => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={onSelectAll}
                  className="w-4 h-4 text-primary-600 dark:text-primary-400 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-2"
                />
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => onSort('nome')}>
                <div className="flex items-center gap-1">
                  Nome
                  {sortBy === 'nome' && (
                    <span className="text-primary-600 dark:text-primary-400">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => onSort('nivel')}>
                <div className="flex items-center gap-1">
                  Nível
                  {sortBy === 'nivel' && (
                    <span className="text-primary-600 dark:text-primary-400">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Organizacional
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => onSort('status')}>
                <div className="flex items-center gap-1">
                  Status
                  {sortBy === 'status' && (
                    <span className="text-primary-600 dark:text-primary-400">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => onSort('ultima_atividade')}>
                <div className="flex items-center gap-1">
                  Última Atividade
                  {sortBy === 'ultima_atividade' && (
                    <span className="text-primary-600 dark:text-primary-400">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => onUserSelection(user.id)}
                    className="w-4 h-4 text-primary-600 dark:text-primary-400 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-2"
                  />
                </td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8">
                      {user.avatar ? (
                        <img
                          className="h-8 w-8 rounded-full object-cover"
                          src={`${import.meta.env.VITE_API_URL}/img/avatars/${user.avatar}`}
                          alt={user.nome}
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-sm font-medium">
                          {user.nome.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{user.nome}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                  {getNivelBadge(user.nivel)}
                </td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                  {renderOrganizationalInfo(user)}
                </td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                  {getStatusBadge(user.status)}
                </td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                  {user.ultima_atividade ? new Date(user.ultima_atividade).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : '-'}
                </td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <Link 
                      to={`/usuarios/editar/${user.id}`} 
                      className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 p-1 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 rounded transition-colors"
                      title="Editar usuário"
                    >
                      <Edit className="w-4 h-4" />
                    </Link>
                    <button 
                      onClick={() => onUserDelete(user.id, user.nome)}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                      title="Excluir usuário"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserTable;
